// =============================================================================
// FE-16: Admin Delegation Audit Report Page
// PRD §4 Feature D — filterable table of all delegations across all managers
// Route: /settings/approvals/delegation/audit
// UI pattern: matches MissingTimesheetsReportPage — DataTableEnhanced flat table
// =============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  ClipboardList,
  Download,
  RefreshCw,
  Calendar,
  User,
  Users,
  X,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

import { PageHeader } from '@/components/blocks/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import { Skeleton } from '@/components/ui/skeleton';

import { useDelegationService } from '../hooks/useDelegationService';
import { DelegationStatusBadge } from '../components/DelegationStatusBadge';
import { DelegationErrorState } from '../components/DelegationErrorState';
import { usePermissions } from '@/contexts/PermissionContext';
import { AccessRestricted } from '@/components/shared/AccessRestricted';
import type {
  ApprovalDelegation,
  DelegationAuditFilter,
} from '../types/delegation.types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  try { return format(parseISO(iso), 'd MMM yyyy'); } catch { return iso; }
}

function getDurationLabel(days: number): string {
  return days === 1 ? '1 day' : `${days} days`;
}

// ---------------------------------------------------------------------------
// CSV Export
// ---------------------------------------------------------------------------

function exportToCsv(items: ApprovalDelegation[]): void {
  const rows: string[][] = [
    ['Manager', 'Delegate', 'Start Date', 'End Date', 'Duration (days)', 'Status', 'Notes', 'Revoke Reason'],
  ];
  for (const d of items) {
    rows.push([
      d.managerName,
      d.delegateName,
      d.startDate,
      d.endDate,
      String(d.durationDays),
      d.status,
      d.notes ?? '',
      d.revokeReason ?? '',
    ]);
  }
  const csv = rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `delegation-audit-${format(new Date(), 'yyyy-MM-dd')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export const DelegationAuditPage: React.FC = () => {
  const { service: delegationService } = useDelegationService('approval-audit');
  const { canManageApprovalWorkflows } = usePermissions();
  const [allItems, setAllItems] = useState<ApprovalDelegation[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pre-filter data before passing to table — bypasses DataTableEnhanced
  // globalFilterFn limitation which only searches single column values
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return allItems;
    const q = searchQuery.toLowerCase();
    return allItems.filter(
      (d) =>
        (d.managerName ?? '').toLowerCase().includes(q) ||
        (d.delegateName ?? '').toLowerCase().includes(q)
    );
  }, [allItems, searchQuery]);

  // ---------------------------------------------------------------------------
  // Data loading — flatten to ApprovalDelegation[] for clean table access
  // ---------------------------------------------------------------------------

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filter: DelegationAuditFilter = { page: 1, pageSize: 200 };
      const response = await delegationService.getAdminAuditLog(filter);
      setAllItems(response.items.map((i) => i.delegation));
    } catch (err) {
      console.error('[DelegationAuditPage] load error', err);
      setError('Failed to load delegation audit data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [delegationService]);

  useEffect(() => { load(); }, [load]);

  // ---------------------------------------------------------------------------
  // Export handler
  // ---------------------------------------------------------------------------

  const handleExport = useCallback(() => {
    try {
      exportToCsv(allItems);
      toast.success(`Exported ${allItems.length} delegation record${allItems.length !== 1 ? 's' : ''} to CSV.`);
    } catch {
      toast.error('Export failed. Please try again.');
    }
  }, [allItems]);

  // ---------------------------------------------------------------------------
  // Columns — flat accessors on ApprovalDelegation directly, so global search works
  // ---------------------------------------------------------------------------

  const columns: ColumnDef<ApprovalDelegation>[] = useMemo(() => [
    {
      id: 'managerName',
      accessorKey: 'managerName',
      header: 'Manager',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="font-medium">{row.original.managerName}</span>
        </div>
      ),
      enableSorting: true,
      enableGrouping: true,
      enableGlobalFilter: true,
    },
    {
      id: 'delegateName',
      accessorKey: 'delegateName',
      header: 'Delegate',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span>{row.original.delegateName}</span>
        </div>
      ),
      enableSorting: true,
      enableGrouping: true,
      enableGlobalFilter: true,
    },
    {
      id: 'dateRange',
      accessorKey: 'startDate',
      header: 'Date Range',
      cell: ({ row }) => {
        const { startDate, endDate, durationDays } = row.original;
        return (
          <div className="flex items-center gap-1.5 text-sm whitespace-nowrap">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span>{formatDate(startDate)} – {formatDate(endDate)}</span>
            <span className="text-xs text-muted-foreground">({getDurationLabel(durationDays)})</span>
          </div>
        );
      },
      enableSorting: true,
      enableGrouping: false,
      enableGlobalFilter: false,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <DelegationStatusBadge status={row.original.status} />,
      enableSorting: true,
      enableGrouping: true,
      enableGlobalFilter: true,
    },
    {
      id: 'notes',
      accessorKey: 'notes',
      header: 'Notes',
      cell: ({ row }) => {
        const notes = row.original.notes;
        return notes
          ? <span className="text-sm">{notes}</span>
          : <span className="text-sm text-muted-foreground">—</span>;
      },
      enableSorting: false,
      enableGrouping: false,
      enableGlobalFilter: false,
    },
    {
      id: 'revokeReason',
      accessorKey: 'revokeReason',
      header: 'Revoke Reason',
      cell: ({ row }) => {
        const reason = row.original.revokeReason;
        return reason
          ? <span className="text-sm">{reason}</span>
          : <span className="text-sm text-muted-foreground">—</span>;
      },
      enableSorting: false,
      enableGrouping: false,
      enableGlobalFilter: false,
    },
  ], []);

  // ---------------------------------------------------------------------------
  // Field labels & categories
  // ---------------------------------------------------------------------------

  const fieldLabels: Record<string, string> = {
    managerName:  'Manager',
    delegateName: 'Delegate',
    dateRange:    'Date Range',
    status:       'Status',
    notes:        'Notes',
    revokeReason: 'Revoke Reason',
  };

  const fieldCategories: Record<string, string> = {
    managerName:  'People',
    delegateName: 'People',
    dateRange:    'Time Period',
    status:       'Status',
    notes:        'Detail',
    revokeReason: 'Detail',
  };

  // ---------------------------------------------------------------------------
  // Access denied guard
  // ---------------------------------------------------------------------------

  if (!canManageApprovalWorkflows()) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title="Delegation Audit Report"

          icon={<ClipboardList className="h-5 w-5" />}
          showBreadcrumb
          breadcrumbItems={[
            { label: 'Settings', href: '/settings/general' },
            { label: 'Approvals', href: '/settings/approvals' },
            { label: 'Delegation Audit' },
          ]}
          showActions={false}
        />
        <AccessRestricted message="You don't have permission to view the delegation audit report. Contact your administrator to request access." />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div>
          <Skeleton className="h-10 w-72 mb-3" />
          <Skeleton className="h-5 w-96" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Error state
  // ---------------------------------------------------------------------------

  if (error) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title="Delegation Audit Report"

          icon={<ClipboardList className="h-5 w-5" />}
          showBreadcrumb
          breadcrumbItems={[
            { label: 'Settings', href: '/settings/general' },
            { label: 'Approvals', href: '/settings/approvals' },
            { label: 'Delegation Audit' },
          ]}
          showActions={false}
        />
        <DelegationErrorState message={error} onRetry={load} mode="alert" />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6 p-6">
      {/* Page header */}
      <PageHeader
        title="Delegation Audit Report"

        icon={<ClipboardList className="h-5 w-5" />}
        showBreadcrumb
        breadcrumbItems={[
          { label: 'Settings', href: '/settings/general' },
          { label: 'Approvals', href: '/settings/approvals' },
          { label: 'Delegation Audit' },
        ]}
        showActions={false}
      />

      {/* Main table */}
      <Card>
        <CardContent className="pt-6">
          <DataTableEnhanced
            columns={columns}
            data={filteredItems}
            searchPlaceholder="Search by manager or delegate name..."
            enableGrouping={true}
            enableColumnVisibility={true}
            enableSorting={true}
            enableFiltering={true}
            enableRowSelection={false}
            pageSize={20}
            storageKey="delegation-audit-report"
            fieldLabels={fieldLabels}
            fieldCategories={fieldCategories}
            showSortIconsWhenNotSorted={false}
            customFilters={
              <div className="relative max-w-sm">
                <Input
                  placeholder="Search by manager or delegate name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pr-8"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-2 hover:bg-transparent"
                    onClick={() => setSearchQuery('')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            }
            toolbarActions={
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={load}
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={allItems.length === 0}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </Button>
              </>
            }
          />
        </CardContent>
      </Card>
    </div>
  );
};
